import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import PageHero from "../components/blocks/PageHero";
import { CONTACT } from "../lib/email";
import { contactAPI } from "../services/api";
import PremiumButton from "../components/ui/PremiumButton";
import useSEO from "../hooks/useSEO";

const Contact = () => {
  useSEO('/contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.submit({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message,
      });

      toast.success("Message received — we'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (e) {
      toast.error("Could not submit right now. Please try calling us instead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050706] pt-16">
      <PageHero
        title="Contact"
        subtitle="Let's start planning your retreat"
        image="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=2400&q=80"
        heightClass="h-[52vh]"
      />

      {/* Contact Info & Form */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl md:text-4xl font-light mb-6 text-white">Get in touch</h2>
              <p className="text-sm md:text-base text-white/70 leading-relaxed mb-8">
                Have questions about availability, pricing, or what to expect? Reach out via any channel
                below and we'll respond as quickly as possible.
              </p>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="rounded-full border border-white/10 bg-white/5 p-3">
                    <MapPin size={22} className="text-white/80" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Location</h3>
                    <p className="text-white/70">{CONTACT.fullAddress}</p>
                  </div>
                </div>

                {/* Booking Phone - Both numbers */}
                <div className="flex items-start space-x-4">
                  <div className="rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 p-3">
                    <Phone size={22} className="text-[#C9A227]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Booking Helpline</h3>
                    <a
                      href={`tel:${CONTACT.primaryPhone.replace(/-/g, "")}`}
                      className="block text-[#C9A227] hover:text-white transition font-medium text-lg"
                    >
                      {CONTACT.primaryPhone}
                    </a>
                    <a
                      href={`tel:${CONTACT.supportPhone.replace(/-/g, "")}`}
                      className="block text-[#C9A227] hover:text-white transition font-medium text-lg mt-1"
                    >
                      {CONTACT.supportPhone}
                    </a>
                  </div>
                </div>

                {/* Booking Email */}
                <div className="flex items-start space-x-4">
                  <div className="rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 p-3">
                    <Mail size={22} className="text-[#C9A227]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">For Bookings</h3>
                    <a
                      href={`mailto:${CONTACT.emails.booking}`}
                      className="block text-[#C9A227] hover:text-white transition"
                    >
                      {CONTACT.emails.booking}
                    </a>
                  </div>
                </div>

                {/* Official & Business Emails */}
                <div className="flex items-start space-x-4">
                  <div className="rounded-full border border-white/10 bg-white/5 p-3">
                    <Mail size={22} className="text-white/80" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Official & Business Inquiries</h3>
                    <a
                      href={`mailto:${CONTACT.emails.operations}`}
                      className="block text-white/70 hover:text-white transition"
                    >
                      {CONTACT.emails.operations}
                    </a>
                    <a
                      href={`mailto:${CONTACT.emails.admin}`}
                      className="block text-white/70 hover:text-white transition mt-1"
                    >
                      {CONTACT.emails.admin}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start space-x-4">
                  <div className="rounded-full border border-white/10 bg-white/5 p-3">
                    <Clock size={22} className="text-white/80" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Response time</h3>
                    <p className="text-white/70">
                      We typically respond within 1–2 hours during business hours (9 AM – 8 PM IST).
                    </p>
                  </div>
                </div>
              </div>

              {/* Single Long Book Now Button */}
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#C9A227] px-6 py-4 text-base font-semibold text-[#111] shadow-[0_12px_28px_rgba(0,0,0,0.35)] hover:bg-[#d4b03a] transition"
                >
                  Book Now
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-light mb-6 text-white">Send a message</h2>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md">
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                      Name <span className="text-[#C9A227]">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                        Email <span className="text-[#C9A227]">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">
                      Subject <span className="text-[#C9A227]">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                    >
                      <option value="">Select a subject...</option>
                      <option value="booking">Booking Inquiry</option>
                      <option value="availability">Check Availability</option>
                      <option value="events">Event Planning</option>
                      <option value="general">General Question</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                      Message <span className="text-[#C9A227]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                      placeholder="Tell us about your plans, questions, or requirements..."
                    ></textarea>
                  </div>

                  <PremiumButton
                    variant="primary"
                    icon={false}
                    className="w-full justify-center py-4"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? "Sending…" : "Send Message"}
                  </PremiumButton>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light mb-8 text-white text-center">
              Find us
            </h2>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3912.1!2d75.8!3d11.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sglowLAWNS+Resorts+%26+Home-stays!5e0!3m2!1sen!2sin!4v1"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Glow Lawns Location"
              ></iframe>
            </div>
            <div className="text-center mt-6">
              <p className="text-sm text-white/70">
                <span className="font-semibold text-white">Location:</span> Thotillpalam, Chappanthottam, Kozhikode, Kavilumpara, Kerala 673513
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4">
                <a 
                  href="https://www.google.com/maps/search/glowLAWNS+Resorts+%26+Home-stays"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/20 bg-white/5 text-white/80 text-sm hover:bg-white/10 hover:border-white/30 transition-all duration-300"
                  data-testid="view-map-btn"
                >
                  <MapPin className="w-4 h-4" />
                  View on Map
                </a>
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=PR2J%2BJV+Kavilumpara,+Kerala"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#C9A227] text-[#111] text-sm font-semibold hover:bg-[#d4b03a] hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(201,162,39,0.25)] transition-all duration-300"
                  data-testid="get-directions-btn"
                >
                  <Navigation className="w-4 h-4" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
